import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../services/apiConfig';
import { fetchCandidateSkills, getMobileNumber, markCandidateSkillsAsRemoved, saveCandidateSkills } from '../services/apiService';


const AddSkillModal = ({ isOpen, onClose, onSaveSkills, currentSkills = [], allActiveSkills = [], mobileNumber: mobileFromProps }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [mySkills, setMySkills] = useState([]); // Current user skills (editable)
    const [systemAddedSkills, setSystemAddedSkills] = useState([]); // System added skills (from inactive API)
    const [suggestedSkills, setSuggestedSkills] = useState([]); // Recommended skills from API
    const [skillsToRemove, setSkillsToRemove] = useState([]); // Track skills to be removed
    const [skillsToAdd, setSkillsToAdd] = useState([]); // Track skills to be added
    const [removedSystemSkillIds, setRemovedSystemSkillIds] = useState([]); // Track removed system-added skill IDs
    const [isSaving, setIsSaving] = useState(false); // Track API execution state
    const [isLoadingRecommended, setIsLoadingRecommended] = useState(false); // Track recommended skills loading state
    const [activeTab, setActiveTab] = useState('mySkills'); // Tab state: 'mySkills', 'suggested', 'allSkills'

    // Toast notification function
    const showToast = (message, type = 'success') => {
        // Create toast element
        const toast = document.createElement('div');
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            padding: 16px 24px;
            border-radius: 8px;
            color: white;
            font-weight: 500;
            font-size: 14px;
            z-index: 10000;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            transform: translateX(100%);
            transition: transform 0.3s ease-in-out;
            max-width: 300px;
            word-wrap: break-word;
            ${type === 'success' 
                ? 'background: linear-gradient(135deg, #10b981, #059669);' 
                : 'background: linear-gradient(135deg, #ef4444, #dc2626);'
            }
        `;
        
        // Add icon and message
        const icon = type === 'success' ? '✓' : '✕';
        toast.innerHTML = `<span style="margin-right: 8px;">${icon}</span>${message}`;
        
        // Add to document
        document.body.appendChild(toast);
        
        // Animate in
        setTimeout(() => {
            toast.style.transform = 'translateX(0)';
        }, 100);
        
        // Remove after 3 seconds
        setTimeout(() => {
            toast.style.transform = 'translateX(100%)';
            setTimeout(() => {
                if (document.body.contains(toast)) {
                    document.body.removeChild(toast);
                }
            }, 300);
        }, 3000);
    };

    // Fetch inactive skills (System Added Skills) and recommended skills when modal opens
    useEffect(() => {
        const loadSkillsData = async () => {
            if (!isOpen) return;
            
            // Ensure modal always opens on "My Skills" tab
            setActiveTab('mySkills');

            // Initialize My Skills from currentSkills prop
            const mySkillsData = currentSkills.map((skillName, index) => {
                const matchingSkill = allActiveSkills.find(skill => 
                    getSkillName(skill) === skillName
                );
                return {
                    id: matchingSkill ? getSkillId(matchingSkill) : `temp-${index}`,
                    name: skillName,
                    isUserAdded: true
                };
            });
            setMySkills(mySkillsData);
            
            // Fetch System Added Skills (inactive skills)
            try {
                const mobileNumber = (mobileFromProps && String(mobileFromProps).trim()) || getMobileNumber();
                
                console.log('[AddSkillModal] Fetching inactive skills (without fallback) for mobile:', mobileNumber);
                
                const response = await fetch(`${API_ENDPOINTS.fetchInactiveCandidateSkillsWithoutFallback}?mobileNumber=${mobileNumber}`, {
                    method: 'GET'
                });
                
                if (response.ok) {
                    const result = await response.json();
                    console.log('[AddSkillModal] Fetched inactive skills response:', result);
                    console.log('[AddSkillModal] Is array?', Array.isArray(result));
                    
                    if (result && Array.isArray(result) && result.length > 0) {
                        const systemSkills = result.map(skill => ({
                            id: skill.skillId || skill.id,
                            name: skill.skillName || skill.name,
                            isSystemAdded: true
                        }));
                        console.log('[AddSkillModal] Setting system added skills:', systemSkills);
                        setSystemAddedSkills(systemSkills);
                    } else {
                        console.log('[AddSkillModal] No inactive skills found or empty array');
                        setSystemAddedSkills([]);
                    }
                } else {
                    console.error('[AddSkillModal] Failed to fetch inactive skills:', response.status, response.statusText);
                    setSystemAddedSkills([]);
                }
            } catch (error) {
                console.error('[AddSkillModal] Error fetching inactive skills:', error);
                setSystemAddedSkills([]);
            }
            
            // Fetch Recommended Skills (Suggested Skills)
            try {
                const mobileNumber = (mobileFromProps && String(mobileFromProps).trim()) || getMobileNumber();
                
                console.log('[AddSkillModal] Fetching recommended skills for mobile:', mobileNumber);
                setIsLoadingRecommended(true);
                
                const response = await fetch(`${API_ENDPOINTS.fetchRecommendedSkillsForCandidate}?mobileNumber=${mobileNumber}`, {
                    method: 'GET'
                });
                
                if (response.ok) {
                    const result = await response.json();
                    console.log('[AddSkillModal] Fetched recommended skills:', result);
                    
                    if (result && Array.isArray(result)) {
                        const recommended = result.map(skill => ({
                            id: skill.skillId || skill.id,
                            name: skill.skillName || skill.name,
                            isRecommended: true
                        }));
                        setSuggestedSkills(recommended);
                    }
                } else {
                    console.error('[AddSkillModal] Failed to fetch recommended skills:', response.status, response.statusText);
                }
            } catch (error) {
                console.error('[AddSkillModal] Error fetching recommended skills:', error);
            } finally {
                setIsLoadingRecommended(false);
            }
        };
        
        loadSkillsData();
    }, [isOpen, currentSkills, allActiveSkills, mobileFromProps]);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSkillsToRemove([]);
            setSkillsToAdd([]);
            setRemovedSystemSkillIds([]);
            setSearchTerm('');
            setActiveTab('mySkills');
        }
    }, [isOpen]);

    // Helper function to get skill name from skill object or string
    const getSkillName = (skill) => {
        if (typeof skill === 'string') return skill;
        return skill.skillName || skill.name || skill.title || skill.label || `Skill ${skill.id || 'Unknown'}`;
    };

    // Helper function to get skill ID from skill object
    const getSkillId = (skill) => {
        if (typeof skill === 'string') return skill; // Use name as ID for string skills
        return skill.id || skill.skillId || skill.name || skill;
    };

    // Check if a skill is already owned by the user (but still show it as selectable)
    // const isCurrentSkill = (skill) => {
    //     const skillName = getSkillName(skill);
    //     return currentSkillNames.includes(skillName);
    // };

    // Handle removing skill from My Skills or System Added Skills
    const handleRemoveSkill = (skillId, skillName, isSystemAdded = false) => {
        console.log('[AddSkillModal] Removing skill:', { skillId, skillName, isSystemAdded });
        
        if (isSystemAdded) {
            // Remove from system added skills display
            setSystemAddedSkills(prev => prev.filter(s => s.id !== skillId));
            
            // Track removed system skill IDs separately for API call
            setRemovedSystemSkillIds(prev => {
                if (!prev.includes(skillId)) {
                    console.log('[AddSkillModal] Adding system skill to removal list:', skillId);
                    return [...prev, skillId];
                }
                return prev;
            });
        } else {
            // Remove from my skills display
            setMySkills(prev => prev.filter(s => s.id !== skillId));
        }
        
        // Add to removal list
        setSkillsToRemove(prev => {
            if (!prev.includes(skillId)) {
                return [...prev, skillId];
            }
            return prev;
        });
        
        // Remove from add list if it was there
        setSkillsToAdd(prev => prev.filter(id => id !== skillId));
    };
    
    // Handle adding skill from Suggested Skills, System Added Skills, or All Skills
    const handleAddSkill = (skillId, skillName, fromSystemAdded = false) => {
        console.log('[AddSkillModal] Adding skill:', { skillId, skillName, fromSystemAdded });
        
        // Add to my skills display
        setMySkills(prev => {
            // Check if already exists
            if (prev.some(s => s.id === skillId)) {
                return prev;
            }
            return [...prev, { id: skillId, name: skillName, isUserAdded: true }];
        });
        
        // Remove from suggested skills if it was there
        setSuggestedSkills(prev => prev.filter(s => s.id !== skillId));
        
        // Remove from system added skills if it was there
        setSystemAddedSkills(prev => prev.filter(s => s.id !== skillId));
        
        // If moving from system added skills, remove from the removal tracking list
        if (fromSystemAdded) {
            setRemovedSystemSkillIds(prev => prev.filter(id => id !== skillId));
        }
        
        // Add to addition list
        setSkillsToAdd(prev => {
            if (!prev.includes(skillId)) {
                return [...prev, skillId];
            }
            return prev;
        });
        
        // Remove from removal list if it was there
        setSkillsToRemove(prev => prev.filter(id => id !== skillId));
    };
    
    // Filter All Skills - exclude skills already in My Skills
    const getFilteredAllSkills = () => {
        const mySkillIds = mySkills.map(s => s.id);
        
        return allActiveSkills
            .filter(skill => {
                const skillId = getSkillId(skill);
                const skillName = getSkillName(skill);
                const matchesSearch = skillName.toLowerCase().includes(searchTerm.toLowerCase());
                const notInMySkills = !mySkillIds.includes(skillId);
                return matchesSearch && notInMySkills;
            })
            .sort((a, b) => {
                const nameA = getSkillName(a).toLowerCase();
                const nameB = getSkillName(b).toLowerCase();
                return nameA.localeCompare(nameB);
            });
    };

    const handleSave = async () => {
        // Prevent multiple simultaneous API calls
        if (isSaving) {
            return;
        }

        // Validate that there are changes to save
        if (skillsToAdd.length === 0 && skillsToRemove.length === 0 && removedSystemSkillIds.length === 0) {
            showToast('No changes to save', 'error');
            return;
        }

        try {
            setIsSaving(true); // Set loading state
            
            // Get mobile number from props or apiService fallback
            const mobileNumber = (mobileFromProps && String(mobileFromProps).trim()) || getMobileNumber();
            if (!mobileNumber || mobileNumber === '0') {
                showToast('Mobile number not found. Please sign in again.', 'error');
                setIsSaving(false);
                return;
            }
            
            // Step 1: Call markCandidateSkillsAsRemoved API if there are removed system skills
            if (removedSystemSkillIds.length > 0) {
                console.log('[AddSkillModal] Marking system skills as removed:', removedSystemSkillIds);
                
                const removeResponse = await markCandidateSkillsAsRemoved(removedSystemSkillIds);
                
                if (removeResponse.success) {
                    console.log('[AddSkillModal] ✅ Successfully marked system skills as removed');
                    // Clear the removed system skills list after successful API call
                    setRemovedSystemSkillIds([]);
                } else {
                    console.error('[AddSkillModal] ❌ Failed to mark system skills as removed:', removeResponse.error);
                    showToast('Failed to remove system skills. Please try again.', 'error');
                    setIsSaving(false);
                    return;
                }
            }
            
            // Step 2: Prepare final skill list (names/objects) for persistence
            const finalSkillsSnapshot = mySkills
                .filter(skill => skill && skill.name)
                .map(skill => ({ id: skill.id, name: skill.name }));

            if (finalSkillsSnapshot.length === 0) {
                showToast('No valid skills to save', 'error');
                setIsSaving(false);
                return;
            }

            const mappingSources = [
                ...(Array.isArray(allActiveSkills) ? allActiveSkills : []),
                ...systemAddedSkills,
                ...suggestedSkills
            ];

            console.log('[AddSkillModal] Saving skills via apiService:', {
                finalSkillsSnapshot,
                mappingSourcesCount: mappingSources.length,
                removedSystemSkillIds: removedSystemSkillIds.length > 0 ? 'Already processed' : 'None'
            });

            // Step 3: Call centralized saveCandidateSkills helper (handles auth + payload)
            const saveResponse = await saveCandidateSkills(finalSkillsSnapshot, mappingSources);
            
            if (saveResponse.success) {
                console.log('[AddSkillModal] API Response:', saveResponse);
                
                // Show success toast
                showToast(
                    (typeof saveResponse.data === 'string' && saveResponse.data) ||
                    'Skills saved successfully!',
                    'success'
                );
                
                // Fetch updated skills
                try {
                    console.log('[AddSkillModal] Fetching updated candidate skills...');
                    const skillsResult = await fetchCandidateSkills();
                    console.log('[AddSkillModal] Updated skills fetched:', skillsResult);
                    
                    // Notify parent component to refresh skills display
                    if (skillsResult.success && skillsResult.data) {
                        onSaveSkills(skillsResult.data);
                    }
                } catch (error) {
                    console.error('[AddSkillModal] Error fetching updated skills:', error);
                }
                
                // Reset modal state and close
                setSkillsToAdd([]);
                setSkillsToRemove([]);
                setRemovedSystemSkillIds([]);
                setSearchTerm('');
                onClose();
            } else {
                console.error('[AddSkillModal] API Error:', saveResponse);
                showToast(saveResponse.error || 'Failed to save skills. Please try again.', 'error');
            }
        } catch (error) {
            console.error('[AddSkillModal] Error saving skills:', error);
            showToast('Error saving skills: ' + error.message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClose = () => {
        // Reset all state when closing without saving
        setSkillsToAdd([]);
        setSkillsToRemove([]);
        setRemovedSystemSkillIds([]);
        setSearchTerm('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
            <div style={{
            display: 'flex',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            justifyContent: 'center',
            alignItems: 'center'
        }}>
            <div style={{
                background: 'white',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '800px',
                maxHeight: '90vh',
                boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #2563eb, #3b82f6)',
                    color: 'white',
                    padding: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{
                        margin: 0,
                        fontSize: '1.5rem',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <i className="fas fa-lightbulb" style={{ fontSize: '1.25rem' }}></i>
                        Add Skills
                    </h3>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={handleClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '22px',
                            cursor: 'pointer',
                            color: 'white',
                            padding: 0,
                            borderRadius: '6px',
                            width: '44px',
                            height: '44px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'background 0.2s, box-shadow 0.2s'
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                        onMouseOut={(e) => (e.currentTarget.style.background = 'none')}
                        onFocus={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.5), 0 0 0 6px rgba(37, 99, 235, 0.5)';
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.background = 'none';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>
                
                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    borderBottom: '2px solid #e5e7eb',
                    background: '#f9fafb'
                }}>
                    <button
                        onClick={() => setActiveTab('mySkills')}
                        style={{
                            flex: 1,
                            padding: '1rem',
                            background: activeTab === 'mySkills' ? 'white' : 'transparent',
                            color: activeTab === 'mySkills' ? '#2563eb' : '#6b7280',
                            border: 'none',
                            borderBottom: activeTab === 'mySkills' ? '3px solid #2563eb' : '3px solid transparent',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: activeTab === 'mySkills' ? '600' : '500',
                            transition: 'all 0.2s',
                            marginBottom: '-2px'
                        }}
                    >
                        My Skills
                    </button>
                    <button
                        onClick={() => setActiveTab('suggested')}
                        style={{
                            flex: 1,
                            padding: '1rem',
                            background: activeTab === 'suggested' ? 'white' : 'transparent',
                            color: activeTab === 'suggested' ? '#2563eb' : '#6b7280',
                            border: 'none',
                            borderBottom: activeTab === 'suggested' ? '3px solid #2563eb' : '3px solid transparent',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: activeTab === 'suggested' ? '600' : '500',
                            transition: 'all 0.2s',
                            marginBottom: '-2px'
                        }}
                    >
                        Suggested Skills
                    </button>
                    <button
                        onClick={() => setActiveTab('allSkills')}
                        style={{
                            flex: 1,
                            padding: '1rem',
                            background: activeTab === 'allSkills' ? 'white' : 'transparent',
                            color: activeTab === 'allSkills' ? '#2563eb' : '#6b7280',
                            border: 'none',
                            borderBottom: activeTab === 'allSkills' ? '3px solid #2563eb' : '3px solid transparent',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: activeTab === 'allSkills' ? '600' : '500',
                            transition: 'all 0.2s',
                            marginBottom: '-2px'
                        }}
                    >
                        All Skills
                    </button>
                </div>

                {/* Content */}
                <div style={{
                    padding: '1.5rem',
                    maxHeight: 'calc(80vh - 180px)',
                    overflowY: 'auto'
                }}>
                    {/* Info message for My Skills tab */}
                    {activeTab === 'mySkills' && (
                        <div style={{
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderLeft: '4px solid #2563eb',
                            borderRadius: '6px',
                            padding: '1rem',
                            marginBottom: '1.5rem',
                            color: '#1e40af',
                            fontSize: '0.9rem',
                            lineHeight: '1.5'
                        }}>
                            Manage your skills here. Tap × to remove any skill from your profile.
                        </div>
                    )}
                    
                    {/* Info message for Suggested Skills tab */}
                    {activeTab === 'suggested' && (
                        <div style={{
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderLeft: '4px solid #2563eb',
                            borderRadius: '6px',
                            padding: '1rem',
                            marginBottom: '1.5rem',
                            color: '#1e40af',
                            fontSize: '0.9rem',
                            lineHeight: '1.5'
                        }}>
                            We suggest these skills based on your profile. Tap any skill to add it to your profile.
                        </div>
                    )}
                    
                    {/* Info message for All Skills tab */}
                    {activeTab === 'allSkills' && (
                        <div style={{
                            background: '#eff6ff',
                            border: '1px solid #bfdbfe',
                            borderLeft: '4px solid #2563eb',
                            borderRadius: '6px',
                            padding: '1rem',
                            marginBottom: '1.5rem',
                            color: '#1e40af',
                            fontSize: '0.9rem',
                            lineHeight: '1.5'
                        }}>
                            Browse our complete skills library. Tap any skill to add it to your profile.
                        </div>
                    )}
                    
                    {/* Search bar - only show for All Skills tab */}
                    {activeTab === 'allSkills' && (
                        <input
                            type="text"
                            placeholder="Search skills..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                fontSize: '0.9rem',
                                marginBottom: '1.5rem',
                                boxSizing: 'border-box',
                                outline: 'none'
                            }}
                        />
                    )}
                    
                    {/* My Skills Tab (includes both My Skills and System Added Skills) */}
                    {activeTab === 'mySkills' && (
                        <>
                            {/* My Skills Section */}
                            <h4 style={{
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#374151',
                                marginBottom: '0.75rem',
                                marginTop: '0'
                            }}>
                                My Skills
                            </h4>
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                gap: '0.6rem',
                                lineHeight: '1.4',
                                marginBottom: '1.5rem'
                            }}>
                                {mySkills.length === 0 ? (
                                    <div style={{
                                        textAlign: 'center',
                                        color: '#6b7280',
                                        padding: '1rem',
                                        fontStyle: 'italic',
                                        width: '100%',
                                        fontSize: '0.85rem'
                                    }}>
                                        No skills added yet. Switch to other tabs to add skills.
                                    </div>
                                ) : (
                                    mySkills.map((skill) => (
                                        <div
                                            key={skill.id}
                                            style={{
                                                padding: '0.55rem 2px',
                                                background: '#dbeafe',
                                                color: '#2563eb',
                                                border: '1px solid #bfdbfe',
                                                borderRadius: '12px',
                                                fontSize: '0.85rem',
                                                fontWeight: '500',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                margin: '0',
                                                textAlign: 'center',
                                                verticalAlign: 'top'
                                            }}
                                        >
                                            <span style={{ paddingLeft: '0.7rem' }}>{skill.name}</span>
                                            <button
                                                onClick={() => handleRemoveSkill(skill.id, skill.name, false)}
                                                style={{
                                                    background: '#2563eb',
                                                    border: 'none',
                                                    borderRadius: '50%',
                                                    width: '20px',
                                                    height: '20px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    cursor: 'pointer',
                                                    padding: '0',
                                                    marginRight: '0.3rem',
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                    transition: 'background 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                                                onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Separator */}
                            {systemAddedSkills.length > 0 && (
                                <div style={{
                                    borderTop: '1px solid #e5e7eb',
                                    marginBottom: '1.5rem'
                                }}></div>
                            )}

                            {/* System Added Skills Section */}
                            {systemAddedSkills.length > 0 && (
                                <>
                                    <h4 style={{
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        color: '#374151',
                                        marginBottom: '0.75rem',
                                        marginTop: '0'
                                    }}>
                                        System Added Skills
                                    </h4>
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        justifyContent: 'flex-start',
                                        alignItems: 'flex-start',
                                        gap: '0.6rem',
                                        lineHeight: '1.4'
                                    }}>
                                        {systemAddedSkills.map((skill) => (
                                            <div
                                                key={skill.id}
                                                style={{
                                                    padding: '0.55rem 2px',
                                                    background: '#dbeafe',
                                                    color: '#2563eb',
                                                    border: '1px solid #bfdbfe',
                                                    borderRadius: '12px',
                                                    fontSize: '0.85rem',
                                                    fontWeight: '500',
                                                    whiteSpace: 'nowrap',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    margin: '0',
                                                    textAlign: 'center',
                                                    verticalAlign: 'top'
                                                }}
                                            >
                                                <button
                                                    onClick={() => handleAddSkill(skill.id, skill.name, true)}
                                                    style={{
                                                        background: 'transparent',
                                                        border: 'none',
                                                        color: '#2563eb',
                                                        cursor: 'pointer',
                                                        padding: '0',
                                                        paddingLeft: '0.7rem',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '500',
                                                        transition: 'opacity 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
                                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                                                    title="Click to move to My Skills"
                                                >
                                                    {skill.name}
                                                </button>
                                                <button
                                                    onClick={() => handleRemoveSkill(skill.id, skill.name, true)}
                                                    style={{
                                                        background: '#2563eb',
                                                        border: 'none',
                                                        borderRadius: '50%',
                                                        width: '20px',
                                                        height: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        cursor: 'pointer',
                                                        padding: '0',
                                                        marginRight: '0.3rem',
                                                        color: 'white',
                                                        fontSize: '0.7rem',
                                                        transition: 'background 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = '#1d4ed8'}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = '#2563eb'}
                                                    title="Remove skill"
                                                >
                                                    <i className="fas fa-times"></i>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                        </>
                    )}

                    {/* Suggested Skills Tab */}
                    {activeTab === 'suggested' && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            gap: '0.6rem',
                            lineHeight: '1.4'
                        }}>
                            {isLoadingRecommended ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '2rem',
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '1rem'
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '4px solid #e5e7eb',
                                        borderTop: '4px solid #2563eb',
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite'
                                    }}></div>
                                    <div style={{
                                        color: '#6b7280',
                                        fontSize: '0.9rem',
                                        fontWeight: '500'
                                    }}>
                                        Loading recommended skills...
                                    </div>
                                </div>
                            ) : suggestedSkills.length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    padding: '2rem',
                                    fontStyle: 'italic',
                                    width: '100%'
                                }}>
                                    No suggested skills available.
                                </div>
                            ) : (
                                suggestedSkills.map((skill) => (
                                    <button
                                        key={skill.id}
                                        onClick={() => handleAddSkill(skill.id, skill.name)}
                                        style={{
                                            padding: '0.55rem 2px',
                                            background: 'rgba(249, 115, 22, 0.1)',
                                            color: '#ea580c',
                                            border: '1px solid rgba(249, 115, 22, 0.4)',
                                            borderRadius: '12px',
                                            cursor: 'pointer',
                                            fontSize: '0.85rem',
                                            fontWeight: '500',
                                            transition: 'all 0.2s',
                                            whiteSpace: 'nowrap',
                                            display: 'inline-block',
                                            margin: '0',
                                            textAlign: 'center',
                                            verticalAlign: 'top'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = 'rgba(249, 115, 22, 0.2)';
                                            e.currentTarget.style.borderColor = 'rgba(249, 115, 22, 0.6)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'rgba(249, 115, 22, 0.1)';
                                            e.currentTarget.style.borderColor = 'rgba(249, 115, 22, 0.4)';
                                        }}
                                    >
                                        <span style={{ paddingLeft: '0.7rem', paddingRight: '0.7rem' }}>+ {skill.name}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}

                    {/* All Skills Tab */}
                    {activeTab === 'allSkills' && (
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            gap: '0.6rem',
                            lineHeight: '1.4'
                        }}>
                            {getFilteredAllSkills().length === 0 ? (
                                <div style={{
                                    textAlign: 'center',
                                    color: '#6b7280',
                                    padding: '2rem',
                                    fontStyle: 'italic',
                                    width: '100%'
                                }}>
                                    {searchTerm ? `No skills found matching "${searchTerm}"` : 'All available skills are already added to your profile'}
                                </div>
                            ) : (
                                getFilteredAllSkills().map((skill) => {
                                    const skillName = getSkillName(skill);
                                    const skillId = getSkillId(skill);
                                    
                                    return (
                                        <button
                                            key={skillId}
                                            onClick={() => handleAddSkill(skillId, skillName)}
                                            style={{
                                                padding: '0.55rem 2px',
                                                background: '#f3f4f6',
                                                color: '#4b5563',
                                                border: '1px solid #d1d5db',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                fontSize: '0.85rem',
                                                fontWeight: '500',
                                                transition: 'all 0.2s',
                                                whiteSpace: 'nowrap',
                                                display: 'inline-block',
                                                margin: '0',
                                                textAlign: 'center',
                                                verticalAlign: 'top'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = '#e5e7eb';
                                                e.currentTarget.style.borderColor = '#9ca3af';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = '#f3f4f6';
                                                e.currentTarget.style.borderColor = '#d1d5db';
                                            }}
                                        >
                                            <span style={{ paddingLeft: '0.7rem', paddingRight: '0.7rem' }}>+ {skillName}</span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>
                
                {/* Footer */}
                <div style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #e5e7eb',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        type="button"
                        onClick={handleClose}
                        style={{
                            background: '#f8f9fa',
                            color: '#6b7280',
                            border: '1px solid #d1d5db',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            transition: 'all 0.3s ease',
                            transform: 'scale(1)',
                            width: '120px'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = '#e5e7eb';
                            e.target.style.color = '#374151';
                            e.target.style.borderRadius = '6px';
                            e.target.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = '#f8f9fa';
                            e.target.style.color = '#6b7280';
                            e.target.style.borderRadius = '6px';
                            e.target.style.transform = 'scale(1)';
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        style={{
                            background: isSaving ? '#9ca3af' : '#2563eb',
                            color: 'white',
                            border: isSaving ? '1px solid #9ca3af' : '1px solid #2563eb',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: isSaving ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem',
                            opacity: isSaving ? 0.7 : 1,
                            transition: 'all 0.2s ease',
                            width: '120px'
                        }}
                    >
                        {isSaving ? (
                            <>
                                <div style={{
                                    width: '12px',
                                    height: '12px',
                                    border: '2px solid #ffffff',
                                    borderTop: '2px solid transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}></div>
                                Saving...
                            </>
                        ) : (
                            'Save Skills'
                        )}
                    </button>
                </div>
            </div>
        </div>
        </>
    );
};

export default AddSkillModal;
